// avLightingRates.js
// Shared constants for the non-LED-specific AV & Lighting Group A tools
// (Load Schedule, Zones & Scenes, Device Catalog, AV Planner). LED-specific
// pricing/rates live in ledRates.js — kept separate on purpose.

export const BREAKER_SIZES = [15, 20, 30, 40, 50]; // amps — standard residential/light-commercial
export const VOLTAGES = [120, 240];

// NEC 210.19(A)(1) / 210.20(A) — for continuous loads (operating 3+ hrs, which covers
// almost all lighting), the branch-circuit breaker/conductor must be sized to at least
// 125% of the continuous load. Expressed the other way, that's the same as saying
// continuous load must not exceed 80% of the breaker's rating. This is a DIFFERENT rule
// from the LED driver headroom in ledRates.js (DRIVER_HEADROOM) — that one is about driver
// longevity/heat, this one is a safety/listing requirement for the overcurrent device
// itself. Same 80% number, unrelated reasons — don't conflate them when editing either file.
export const CONTINUOUS_LOAD_FACTOR = 0.8;

// Low-voltage cable types for AV Planner wire schedule — common gauges/types used in
// residential AV distribution. Speaker wire gauge matters for run length/impedance;
// video/network cabling here is the physical medium, not the protocol running over it.
export const CABLE_TYPES = [
  { id: "speaker-16-2", label: "Speaker Wire 16/2 (16 AWG, 2-conductor, CL2/CL3) — single speaker" },
  { id: "speaker-16-4", label: "Speaker Wire 16/4 (16 AWG, 4-conductor, CL2/CL3) — dual speaker taps on one run" },
  { id: "speaker-14-2", label: "Speaker Wire 14/2 (14 AWG, 2-conductor, CL2/CL3) — longer runs/higher power, single speaker" },
  { id: "speaker-14-4", label: "Speaker Wire 14/4 (14 AWG, 4-conductor, CL2/CL3) — longer runs, dual speaker taps" },
  { id: "hdmi", label: "HDMI (direct connect, short runs)" },
  { id: "hdbaset-cat6", label: "Cat6/Cat6a (HDBaseT video extension)" },
  { id: "cat6-network", label: "Cat6/Cat6a (network/control)" },
  { id: "rg6-coax", label: "RG6 Coax (cable/satellite/antenna)" },
];

export const AUDIO_SOURCES = ["Whole-house audio matrix", "Local source (TV/soundbar)", "Streaming device", "Turntable/local input"];
export const VIDEO_SOURCES = ["Whole-house video matrix", "Streaming device", "Cable/satellite box", "Local source (game console, Blu-ray, etc.)"];


// Major panelized/DIN-rail lighting control brands and how many loads their standard
// dimmer module controls — verified against manufacturer spec sheets/product pages
// 2026-07-30, not from memory. Leviton doesn't have a clear panelized-module equivalent
// (mostly in-wall switch/dimmer products), so it's not included here.
export const LIGHTING_CONTROL_SYSTEMS = [
  { id: "custom", label: "Custom / manual", loadsPerModule: null },
  {
    id: "lutron-homeworks-qs",
    label: "Lutron HomeWorks QS",
    loadsPerModule: 4,
    source: "LQSE-series DIN power modules (LQSE-4A, LQSE-4S8, LQSE-4T5/10/20, LQSE-4M, LQSE-4A5) — 4 zones is consistent across the whole lineup, per Lutron spec PDFs.",
  },
  {
    id: "vantage-infusion",
    label: "Vantage InFusion",
    loadsPerModule: 12,
    source: "SDM12-EM Standard Dimmer Module — up to 12 loads, up to 64A across 4 line feeds. Per Legrand/Vantage product page and AV-iQ datasheet.",
  },
  {
    id: "crestron-din",
    label: "Crestron (DIN-1DIMU4)",
    loadsPerModule: 4,
    source: "DIN-1DIMU4 — 4 channels, forward/reverse phase, auto load detect. Per Crestron product page.",
  },
  {
    id: "control4-din",
    label: "Control4 (DIN-8DIM / DIN-8APD)",
    loadsPerModule: 8,
    source: "8-Channel Dimmer / Adaptive Phase Dimmer — 8 channels. Per Control4 datasheet and Snap One product page.",
  },
];
